/**
 * CalendarEditable Component
 * 
 * A fully-featured calendar component for displaying and managing court events.
 * 
 * Features:
 * - Displays court events from the CourtEvent table
 * - Uses react-big-calendar with luxon for date handling
 * - Multiple calendar views (Month, Week, Day, Agenda)
 * - Click events to show detailed modals
 * - Event modal displays both CourtEvent and associated Ticket details
 * - Save and Delete functionality for events
 * 
 * @param props.courtEvents - Array of CourtEventDto objects to display on the calendar
 * @param props.onSave - Callback function called when user saves an event (can be sync or async)
 * @param props.onDelete - Callback function called when user deletes an event (can be sync or async)
 * @param props.onRangeChange - Optional callback function called when user navigates or changes view (date, view)
 * 
 * Usage Example:
 * ```tsx
 * const [events, setEvents] = useState<CourtEventDto[]>([]);
 * 
 * const handleSave = async (event: CourtEventDto) => {
 *   // Handle save logic (can be async)
 *   const success = await saveEventToAPI(event);
 *   return success; // Return true if save was successful
 * };
 * 
 * const handleDelete = async (event: CourtEventDto) => {
 *   // Handle delete logic (can be async)
 *   const success = await deleteEventFromAPI(event);
 *   return success; // Return true if delete was successful
 * };
 * 
 * const handleRangeChange = (date: Date, view: View) => {
 *   // Handle date range changes for loading new events
 *   console.log('New date range:', date, 'View:', view);
 * };
 * 
 * <CalendarEditable 
 *   courtEvents={events}
 *   onSave={handleSave}
 *   onDelete={handleDelete}
 *   onRangeChange={handleRangeChange}
 * />
 * ```
 */

import React, { useState, useMemo } from "react";
import { Calendar, luxonLocalizer, Views, Event, View } from 'react-big-calendar';
import { DateTime } from 'luxon';
import { courtWallClockToMillis, millisToCourtWallClock } from '../../utils/courtEventForm';
import { Link, useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Stack,
  IconButton,
  TextField,
  Tooltip
} from '@mui/material';
import { Edit, Delete, Close, Visibility } from '@mui/icons-material';
import { CourtEventDto, FullCourtEventDto, UpdateCourtEventDto, TicketDto, UserDto } from "../../api/CourtEventApi";
import CalendarEntryApi, {
  CALENDAR_ENTRY_KIND_LABELS,
  CalendarEntryDto,
  ConditionDeadlineDto,
  JuryTrialDateDto,
  CalendarEntryKind,
} from "../../api/CalendarEntryApi";

/**
 * A colour per kind of non-case entry, distinct from the court-setting palette.
 *
 * Time off is the one that has to read differently at a glance: the whole point is that somebody
 * assigning a court date sees the unavailable days without having to click anything.
 */
const ENTRY_COLORS: Record<CalendarEntryKind, string> = {
  appointment: '#6d4c41',
  court: '#37474f',
  time_off: '#8e24aa',
};
import CourtEventApi from "../../api/CourtEventApi";
import AppearanceControl from "../AppearanceControl/AppearanceControl";
import HearingLoginControl from "../HearingLoginControl/HearingLoginControl";
import './CalendarEditable.scss';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { courtEventColor } from '../../utils/courtEventColor';
import CourtDifficultyChip from '../CourtRatingsCard/CourtDifficultyChip';

// Setup the localizer for react-big-calendar using luxon
const localizer = luxonLocalizer(DateTime);

/*
 * Order a day's events by when they start (19 Sep meeting: "we can get the organization,
 * chronological order").
 *
 * react-big-calendar's own rule sorts by DURATION descending before it looks at the time, so an
 * hour-long hearing at 11am sat above a half-hour entry at 8am. Sorting the array we hand it does
 * nothing: it re-sorts internally through this hook, which is the supported way in.
 *
 * Duration still decides between two multi-day events, because those render as one continuous bar
 * across the week and the longest has to take the top level or the bar breaks. Everything else is
 * ordered by start time.
 *
 * `daySpan` is the library's own measure (`localizers/luxon.js`) and is a RAW duration in days, not
 * a count of calendar days. An 8pm-to-1am entry spans 0.2 days, not 2, and `sortWeekEvents` buckets
 * it as an ordinary event. Measuring it any other way puts an overnight entry at the top of the day,
 * which is the very complaint this is meant to fix.
 */
const daySpan = (start: Date, end: Date): number =>
  (end.getTime() - start.getTime()) / 86_400_000;

interface SortedEvent {
  start: Date;
  end: Date;
  allDay?: boolean;
}

function sortByStartTime({ evtA, evtB }: { evtA: SortedEvent; evtB: SortedEvent }): number {
  const dayDiff =
    DateTime.fromJSDate(evtA.start).startOf('day').toMillis() -
    DateTime.fromJSDate(evtB.start).startOf('day').toMillis();
  if (dayDiff) return dayDiff;

  const spanA = daySpan(evtA.start, evtA.end);
  const spanB = daySpan(evtB.start, evtB.end);
  const multiA = spanA > 1;
  const multiB = spanB > 1;

  if (multiA !== multiB) return multiA ? -1 : 1;
  if (multiA && multiB) return spanB - spanA || +evtA.start - +evtB.start;

  if (Boolean(evtA.allDay) !== Boolean(evtB.allDay)) return evtA.allDay ? -1 : 1;

  return +evtA.start - +evtB.start || +evtA.end - +evtB.end;
}

// The shipped @types declare sortEvents as `(a, b) => boolean`. The library calls it with a single
// `{ evtA, evtB }` argument and uses the number it returns (see localizers/luxon.js), so the
// published type is simply wrong and the cast is against the types, not against the runtime.
(localizer as unknown as { sortEvents: typeof sortByStartTime }).sortEvents = sortByStartTime;

/*
 * The time, on the row itself, in month view (19 Sep meeting).
 *
 * The week and day grids place an event by its position, so the time is already readable there, and
 * prefixing it printed it twice. Month view is the one that shows a bare title, and month view is
 * the one staff scan to plan a day.
 *
 * Declared at module scope, not inside the component: a component identity created during render is
 * a different type on every render, so React would unmount and remount every event cell whenever
 * any state changed.
 *
 * `continuesPrior` is the second half of a multi-day event, continuing from an earlier week. Its
 * start time belongs to the first segment, so repeating it there would state a time the event does
 * not begin on that row.
 */
const MonthEventRow = ({
  event,
  title,
  continuesPrior,
}: {
  event: CalendarEvent;
  title: React.ReactNode;
  continuesPrior?: boolean;
}) => {
  if (event.allDay || continuesPrior || !event.start) return <EventLabel event={event} title={title} />;
  return (
    <>
      <span className="calendar-event-time">
        {DateTime.fromJSDate(event.start).toFormat('h:mm a')}
      </span>{' '}
      <EventLabel event={event} title={title} />
    </>
  );
};

/*
 * A warning before the title of a hearing at a difficult court (Lloyd, 6 Oct 2026): "whoever is
 * working the calendar can be aware that in 3 days or in 10 days, we have a hearing for a difficult
 * [court] and should start working sooner than later on it". On every view, so it is seen whichever
 * one staff work from. The score and reasons are in the row's hover text.
 */
const DIFFICULTY_MARK = { watch: '⚠', difficult: '⚠⚠' } as const;

const EventLabel = ({ event, title }: { event: CalendarEvent; title: React.ReactNode }) => {
  const difficulty = event.courtEvent?.CourtEvent_CourtDifficulty;
  if (!difficulty || difficulty.level === 'none') return <>{title}</>;
  return (
    <>
      <span className={`calendar-event-difficulty calendar-event-difficulty--${difficulty.level}`}>
        {DIFFICULTY_MARK[difficulty.level]}
      </span>{' '}
      {title}
    </>
  );
};

/**
 * A row on the calendar: either a court setting or a non-case entry.
 *
 * Exactly one of the two is set. They are kept as separate fields rather than a common shape
 * because they are genuinely different records — a setting belongs to a case and carries the
 * appearance question, an entry belongs to a person and carries none of it — and flattening them
 * would mean inventing a case for Lloyd's dentist appointment.
 */
interface CalendarEvent extends Event {
  courtEvent?: CourtEventDto;
  calendarEntry?: CalendarEntryDto;
  /** A condition deadline read off its case. Read-only here; clicking opens the case. */
  conditionDeadline?: ConditionDeadlineDto;
  /** A jury trial date typed on the case, with no court event that day. Clicking opens the case. */
  juryTrial?: JuryTrialDateDto;
  resource?: any;
}

/** Hoisted so the prop is referentially stable across renders. */
const SCROLL_TO_7AM = new Date(1970, 0, 1, 7, 0, 0);

const CALENDAR_COMPONENTS = {
  event: EventLabel,
  month: { event: MonthEventRow },
};

const CalendarEditable = (props: {
  courtEvents: CourtEventDto[]
  onSave: (event: CourtEventDto) => Promise<boolean> | boolean;
  onDelete: (event: CourtEventDto) => Promise<boolean> | boolean;
  onRangeChange?: (date: Date, view: View) => void;
  /**
   * Non-case entries, shown alongside the settings (Kelsi, Aug 20 2026).
   *
   * Optional so the case screen, which shows one case's settings, is unaffected — it has no use
   * for the firm's time off.
   */
  calendarEntries?: CalendarEntryDto[];
  /** Called after an entry is changed or removed, so the page can reload. */
  onEntryChanged?: () => void;
  /**
   * Outstanding condition deadlines on open cases (Lloyd's step DD, "Condition due dates on the
   * calendar"). Optional for the same reason as calendarEntries.
   */
  conditionDeadlines?: ConditionDeadlineDto[];
  /**
   * Jury trial dates typed on cases (Lloyd, 25 Oct 2026: a Wednesday jury trial "doesn't show up on
   * the calendar"). Optional for the same reason as calendarEntries.
   */
  juryTrials?: JuryTrialDateDto[];
}) => {
  const navigate = useNavigate();
  const [selectedEvent, setSelectedEvent] = useState<CourtEventDto | null>(null);
  const [selectedEntry, setSelectedEntry] = useState<CalendarEntryDto | null>(null);
  const [entrySaving, setEntrySaving] = useState(false);
  const [entryError, setEntryError] = useState<string | null>(null);
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [fullEventData, setFullEventData] = useState<FullCourtEventDto | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    CourtEvent_StartDate: '',
    CourtEvent_StartTime: '',
    CourtEvent_EndDate: '',
    CourtEvent_EndTime: '',
    CourtEvent_Title: '',
    CourtEvent_Desc: '',
  });
  const [currentView, setCurrentView] = useState<View>(Views.MONTH);
  const [currentDate, setCurrentDate] = useState<Date>(new Date());

  // Handlers for calendar navigation and view changes
  const handleNavigate = (date: Date) => {
    setCurrentDate(date);
    if (props.onRangeChange) {
      props.onRangeChange(date, currentView);
    }
  };

  const handleViewChange = (view: View) => {
    setCurrentView(view);
    if (props.onRangeChange) {
      // Use the current date being displayed on the calendar
      props.onRangeChange(currentDate, view);
    }
  };

  // Function to check if the court event form is complete
  // Description is deliberately not required — a court date often has nothing to add beyond the
  // date, time and title. Mirrors courtEventFormProblem in ManagerUserView / ManagerTicketView.
  const isCourtEventFormComplete = () => {
    return editForm.CourtEvent_StartDate.trim() !== '' &&
           editForm.CourtEvent_StartTime.trim() !== '' &&
           editForm.CourtEvent_EndDate.trim() !== '' &&
           editForm.CourtEvent_EndTime.trim() !== '' &&
           editForm.CourtEvent_Title.trim() !== '';
  };

  // Function to check if end date/time is after start date/time
  const isEndTimeAfterStartTime = () => {
    if (!editForm.CourtEvent_StartDate || !editForm.CourtEvent_StartTime || 
        !editForm.CourtEvent_EndDate || !editForm.CourtEvent_EndTime) {
      return true; // Allow if fields are empty (will be caught by other validation)
    }

    const startDateTime = courtWallClockToMillis(editForm.CourtEvent_StartDate, editForm.CourtEvent_StartTime);
    const endDateTime = courtWallClockToMillis(editForm.CourtEvent_EndDate, editForm.CourtEvent_EndTime);
    if (startDateTime === null || endDateTime === null) return false;

    // End time must be strictly after start time (not equal)
    return endDateTime > startDateTime;
  };

  // Function to check if the entire form is valid
  const isCourtEventFormValid = () => {
    return isCourtEventFormComplete() && isEndTimeAfterStartTime();
  };

  // Format event title for display on calendar.
  // No time prefix: react-big-calendar already renders its own "5:00 AM – 5:30 AM" label above the
  // title in week/day view, so prefixing the time printed it twice and pushed the case name out of
  // the visible box.
  const formatEventTitle = (courtEvent: CourtEventDto) => {
    return courtEvent.CourtEvent_Title || 'Court Event';
  };

  // Transform court events and non-case entries into calendar events
  const calendarEvents: CalendarEvent[] = useMemo(() => {
    const settings: CalendarEvent[] = props.courtEvents.map(courtEvent => {
      const startDate = DateTime.fromMillis(parseInt('' + courtEvent.CourtEvent_Start));
      const endDate = DateTime.fromMillis(parseInt('' + courtEvent.CourtEvent_End));

      return {
        id: courtEvent.CourtEvent_guid,
        title: formatEventTitle(courtEvent),
        start: startDate.toJSDate(),
        end: endDate.toJSDate(),
        courtEvent: courtEvent,
        resource: courtEvent
      };
    });

    const entries: CalendarEvent[] = (props.calendarEntries ?? []).map(entry => ({
      id: entry.CalendarEntry_guid,
      // Whose entry it is, on the row itself: "Austin unavailable" means nothing at a glance if
      // three people have time off that week.
      title: entry.CalendarEntry_CreatedByName
        ? `${entry.CalendarEntry_Title} (${entry.CalendarEntry_CreatedByName})`
        : entry.CalendarEntry_Title,
      start: DateTime.fromMillis(entry.CalendarEntry_Start).toJSDate(),
      end: DateTime.fromMillis(entry.CalendarEntry_End).toJSDate(),
      allDay: entry.CalendarEntry_AllDay,
      calendarEntry: entry,
    }));

    // Order is NOT set here. react-big-calendar re-sorts through localizer.sortEvents above, so a
    // sort applied to this array is thrown away — which is what made the first attempt at the
    // chronological ordering look right in the code and wrong on the screen.
    // A deadline is a day, not a moment: all-day on its date, in the calendar's local zone.
    const deadlines: CalendarEvent[] = (props.conditionDeadlines ?? []).map(deadline => {
      const day = DateTime.fromISO(deadline.dueAt).startOf('day');
      return {
        id: `condition-${deadline.conditionGuid}`,
        title: deadline.title,
        start: day.toJSDate(),
        end: day.plus({ days: 1 }).toJSDate(),
        allDay: true,
        conditionDeadline: deadline,
      };
    });

    // The case field holds a day and no time, so a trial is all-day on that date, like a deadline.
    const trials: CalendarEvent[] = (props.juryTrials ?? []).map(trial => {
      const day = DateTime.fromISO(trial.date).startOf('day');
      return {
        id: `jury-trial-${trial.ticketGuid}-${trial.date}`,
        title: trial.title,
        start: day.toJSDate(),
        end: day.plus({ days: 1 }).toJSDate(),
        allDay: true,
        juryTrial: trial,
      };
    });

    return [...settings, ...entries, ...deadlines, ...trials];
  }, [props.courtEvents, props.calendarEntries, props.conditionDeadlines, props.juryTrials]);

  const handleSelectEvent = async (event: CalendarEvent) => {
    // A non-case entry has no case to fetch and none of the case modal's fields apply, so it gets
    // its own, much smaller, dialog.
    if (event.calendarEntry) {
      setSelectedEntry(event.calendarEntry);
      return;
    }
    // A condition is changed on its case, where the rest of it lives, so go there.
    if (event.conditionDeadline) {
      navigate(`/ticket?Ticket_guid=${event.conditionDeadline.ticketGuid}`);
      return;
    }
    // Likewise a trial date: it is the case's field, so it is changed on the case.
    if (event.juryTrial) {
      navigate(`/ticket?Ticket_guid=${event.juryTrial.ticketGuid}`);
      return;
    }
    if (!event.courtEvent) return;

    setSelectedEvent(event.courtEvent);
    setEventModalOpen(true);
    setLoading(true);
    setError(null);
    setFullEventData(null);

    try {
      // Fetch the full event data from the API
      const fullEventData = await CourtEventApi.getCourtEvent(event.courtEvent.CourtEvent_guid);
      setFullEventData(fullEventData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load event details');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setEventModalOpen(false);
    setSelectedEvent(null);
    setFullEventData(null);
    setError(null);
    setSaving(false);
    setIsEditing(false);
    setEditForm({
      CourtEvent_StartDate: '',
      CourtEvent_StartTime: '',
      CourtEvent_EndDate: '',
      CourtEvent_EndTime: '',
      CourtEvent_Title: '',
      CourtEvent_Desc: '',
    });
  };

  const handleEditEvent = () => {
    if (!selectedEvent) return;
    
    // Convert epoch time back to date/time strings for form inputs
    // On the court's clock; see millisToCourtWallClock for the day this used to add.
    const start = millisToCourtWallClock(selectedEvent.CourtEvent_Start);
    const end = millisToCourtWallClock(selectedEvent.CourtEvent_End);

    setEditForm({
      CourtEvent_StartDate: start.date,
      CourtEvent_StartTime: start.time,
      CourtEvent_EndDate: end.date,
      CourtEvent_EndTime: end.time,
      CourtEvent_Title: selectedEvent.CourtEvent_Title,
      CourtEvent_Desc: selectedEvent.CourtEvent_Desc,
    });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditForm({
      CourtEvent_StartDate: '',
      CourtEvent_StartTime: '',
      CourtEvent_EndDate: '',
      CourtEvent_EndTime: '',
      CourtEvent_Title: '',
      CourtEvent_Desc: '',
    });
  };

  const handleSaveEvent = async () => {
    if (!selectedEvent || !isEditing) return;
    
    // Additional validation before submission
    if (!isCourtEventFormValid()) {
      setError('Please ensure all fields are filled correctly and end time is after start time.');
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      
      // Read as court wall-clock, like every other court-date form, not the laptop's zone.
      const startEpoch = courtWallClockToMillis(editForm.CourtEvent_StartDate, editForm.CourtEvent_StartTime);
      const endEpoch = courtWallClockToMillis(editForm.CourtEvent_EndDate, editForm.CourtEvent_EndTime);

      // Double-check that end time is after start time
      if (startEpoch === null || endEpoch === null || endEpoch <= startEpoch) {
        setError('End date and time must be after start date and time.');
        return;
      }

      // Create updated event object
      const updatedEvent: CourtEventDto = {
        ...selectedEvent,
        CourtEvent_Start: startEpoch,
        CourtEvent_End: endEpoch,
        CourtEvent_Title: editForm.CourtEvent_Title,
        CourtEvent_Desc: editForm.CourtEvent_Desc,
      };

      const result = await Promise.resolve(props.onSave(updatedEvent));
      if (result) {
        setSelectedEvent(updatedEvent);
        setIsEditing(false);
        // Update full event data if it exists
        if (fullEventData) {
          setFullEventData({
            ...fullEventData,
            courtEvent: updatedEvent
          });
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = () => {
    setDeleteConfirmOpen(true);
  };

  const handleDeleteEvent = async () => {
    if (!selectedEvent) return;
    
    try {
      setSaving(true);
      const result = await Promise.resolve(props.onDelete(selectedEvent));
      if (result) {
        handleCloseModal();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete event');
    } finally {
      setSaving(false);
      setDeleteConfirmOpen(false);
    }
  };

  const formatDateTime = (timestamp: number) => {
    return DateTime.fromMillis(parseInt('' + timestamp)).toLocaleString(DateTime.DATETIME_MED);
  };

  const eventStyleGetter = (event: CalendarEvent) => {
    // No opacity: 0.8 washed white text out against the fill and cost real contrast.
    const style = {
      backgroundColor: event.calendarEntry
        ? ENTRY_COLORS[event.calendarEntry.CalendarEntry_Kind]
        : event.conditionDeadline
          ? courtEventColor('deadline')
          : event.juryTrial
            ? courtEventColor('trial')
            : courtEventColor(event.courtEvent?.CourtEvent_CalendarCategory),
      borderRadius: '4px',
      color: 'white',
      border: '0px',
      display: 'block',
      padding: '3px 6px'
    };
    return { style };
  };

  /** Native tooltip so a clipped title is still readable on hover, without opening the modal. */
  const eventTooltip = (event: CalendarEvent) => {
    if (event.conditionDeadline) {
      const day = DateTime.fromISO(event.conditionDeadline.dueAt).toFormat('ccc d LLL');
      return `${day} — ${event.conditionDeadline.title}. Click to open the case.`;
    }
    if (event.juryTrial) {
      const day = DateTime.fromISO(event.juryTrial.date).toFormat('ccc d LLL');
      return `${day} — ${event.juryTrial.title} (date from the case; no time recorded). Click to open the case.`;
    }
    if (event.calendarEntry) {
      const entry = event.calendarEntry;
      const start = DateTime.fromMillis(entry.CalendarEntry_Start);
      const when = entry.CalendarEntry_AllDay
        ? start.toFormat('ccc d LLL')
        : start.toFormat('ccc d LLL, h:mm a');
      return `${when} — ${CALENDAR_ENTRY_KIND_LABELS[entry.CalendarEntry_Kind]}: ${entry.CalendarEntry_Title}`;
    }
    const start = DateTime.fromMillis(parseInt('' + event.courtEvent?.CourtEvent_Start));
    const line = `${start.toFormat('ccc d LLL, h:mm a')} — ${event.courtEvent?.CourtEvent_Title || 'Court Event'}`;
    const difficulty = event.courtEvent?.CourtEvent_CourtDifficulty;
    if (!difficulty || difficulty.level === 'none') return line;
    const label = difficulty.level === 'difficult' ? 'Difficult court' : 'Court to watch';
    return `${line}\n${label} (score ${difficulty.score}): ${difficulty.reasons.join(', ')}`;
  };

  return (
    <Box className="calendar-editable">
      <Box className="calendar-container" sx={{ height: 820 }}>
        <Calendar
          localizer={localizer}
          events={calendarEvents}
          startAccessor="start"
          endAccessor="end"
          views={[Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA]}
          defaultView={Views.MONTH}
          view={currentView}
          date={currentDate}
          onNavigate={handleNavigate}
          onView={handleViewChange}
          onSelectEvent={handleSelectEvent}
          eventPropGetter={eventStyleGetter}
          tooltipAccessor={eventTooltip}
          components={CALENDAR_COMPONENTS}
          popup
          showMultiDayTimes
          // One 30-minute slot per labelled hour row rather than two: with two, a 30-minute hearing
          // got roughly 20px of height and the title was sliced through the middle of the letters.
          step={30}
          timeslots={1}
          // Concurrent hearings sit side by side instead of overlapping, so a court date behind
          // another one is still legible rather than half-hidden.
          dayLayoutAlgorithm="no-overlap"
          // Week and day views open at 7 AM, where the hearings are, rather than at midnight with
          // the whole morning below the fold and the grid looking empty.
          scrollToTime={SCROLL_TO_7AM}
          className="custom-calendar"
        />
      </Box>

      {/* Event Details Modal */}
      <Dialog 
        open={eventModalOpen} 
        onClose={handleCloseModal} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { minHeight: '400px' }
        }}
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h5">
              Court Event Details
            </Typography>
            
          </Stack>
        </DialogTitle>
        
        <DialogContent>
          {loading && (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          )}
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {selectedEvent && !loading && (
            <Box>
              {isEditing ? (
                /* Edit Form */
                <Stack spacing={3}>
                  <Typography variant="h6" color="primary">
                    Edit Event Information
                  </Typography>
                  
                  <Stack spacing={2}>
                    <Typography variant="subtitle1" color="primary">
                      Start Date & Time
                    </Typography>
                    <Stack direction="row" spacing={2}>
                      <TextField
                        label="Start Date"
                        type="date"
                        value={editForm.CourtEvent_StartDate}
                        onChange={(e) => {
                          const newStartDate = e.target.value;
                          const newStartTime = editForm.CourtEvent_StartTime;
                          
                          let newEndDate = newStartDate;
                          let newEndTime = editForm.CourtEvent_EndTime;
                          
                          // If we have both start date and start time, calculate end time as 30 minutes later
                          if (newStartDate && newStartTime) {
                            const [hours, minutes] = newStartTime.split(':');
                            const startDateTime = new Date(`${newStartDate}T${newStartTime}`);
                            const endDateTime = new Date(startDateTime.getTime() + 30 * 60000); // Add 30 minutes
                            
                            newEndDate = endDateTime.toISOString().split('T')[0];
                            newEndTime = endDateTime.toTimeString().split(' ')[0].slice(0, 5);
                          } else if (newStartDate && !editForm.CourtEvent_EndDate) {
                            // Auto-populate end date if it's empty (fallback behavior)
                            newEndDate = newStartDate;
                          }
                          
                          setEditForm({ 
                            ...editForm, 
                            CourtEvent_StartDate: newStartDate,
                            CourtEvent_EndDate: newEndDate,
                            CourtEvent_EndTime: newEndTime
                          });
                        }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        required
                        error={editForm.CourtEvent_StartDate.trim() === ''}
                        helperText={editForm.CourtEvent_StartDate.trim() === '' ? 'Start date is required' : ''}
                      />
                      <TextField
                        label="Start Time"
                        type="time"
                        value={editForm.CourtEvent_StartTime}
                        onChange={(e) => {
                          const newStartTime = e.target.value;
                          const newStartDate = editForm.CourtEvent_StartDate;
                          
                          let newEndDate = editForm.CourtEvent_EndDate;
                          let newEndTime = editForm.CourtEvent_EndTime;
                          
                          // If we have both start date and start time, calculate end time as 30 minutes later
                          if (newStartDate && newStartTime) {
                            const startDateTime = new Date(`${newStartDate}T${newStartTime}`);
                            const endDateTime = new Date(startDateTime.getTime() + 30 * 60000); // Add 30 minutes
                            
                            newEndDate = endDateTime.toISOString().split('T')[0];
                            newEndTime = endDateTime.toTimeString().split(' ')[0].slice(0, 5);
                          } else if (newStartTime && !editForm.CourtEvent_EndTime) {
                            // Auto-populate end time if it's empty (fallback: add 30 minutes)
                            const [hours, minutes] = newStartTime.split(':');
                            const totalMinutes = parseInt(hours) * 60 + parseInt(minutes) + 30;
                            const endHour = Math.floor(totalMinutes / 60) % 24;
                            const endMin = totalMinutes % 60;
                            newEndTime = `${endHour.toString().padStart(2, '0')}:${endMin.toString().padStart(2, '0')}`;
                          }
                          
                          setEditForm({ 
                            ...editForm, 
                            CourtEvent_StartTime: newStartTime,
                            CourtEvent_EndDate: newEndDate,
                            CourtEvent_EndTime: newEndTime
                          });
                        }}
                        slotProps={{htmlInput: { step: 900 }}}
                        inputProps={{ step: 900 }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        required
                        error={editForm.CourtEvent_StartTime.trim() === ''}
                        helperText={editForm.CourtEvent_StartTime.trim() === '' ? 'Start time is required' : ''}
                      />
                    </Stack>
                    
                    <Typography variant="subtitle1" color="primary">
                      End Date & Time
                    </Typography>
                    <Stack direction="row" spacing={2}>
                      <TextField
                        label="End Date"
                        type="date"
                        value={editForm.CourtEvent_EndDate}
                        onChange={(e) => setEditForm({ ...editForm, CourtEvent_EndDate: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        required
                        error={editForm.CourtEvent_EndDate.trim() === ''}
                        helperText={editForm.CourtEvent_EndDate.trim() === '' ? 'End date is required' : ''}
                      />
                      <TextField
                        label="End Time"
                        type="time"
                        value={editForm.CourtEvent_EndTime}
                        onChange={(e) => setEditForm({ ...editForm, CourtEvent_EndTime: e.target.value })}
                        slotProps={{htmlInput: { step: 900 }}}
                        inputProps={{ step: 900 }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        required
                        error={editForm.CourtEvent_EndTime.trim() === ''}
                        helperText={editForm.CourtEvent_EndTime.trim() === '' ? 'End time is required' : ''}
                      />
                    </Stack>
                    
                    <TextField
                      label="Event Title"
                      value={editForm.CourtEvent_Title}
                      onChange={(e) => setEditForm({ ...editForm, CourtEvent_Title: e.target.value })}
                      fullWidth
                      required
                      error={editForm.CourtEvent_Title.trim() === ''}
                      helperText={editForm.CourtEvent_Title.trim() === '' ? 'Title is required' : ''}
                    />
                    
                    <TextField
                      label="Description"
                      value={editForm.CourtEvent_Desc}
                      onChange={(e) => setEditForm({ ...editForm, CourtEvent_Desc: e.target.value })}
                      multiline
                      rows={3}
                      fullWidth
                    />
                    
                    {!isEndTimeAfterStartTime() && (
                      <Alert severity="error">
                        End date and time must be after start date and time.
                      </Alert>
                    )}
                  </Stack>
                </Stack>
              ) : (
                /* View Mode */
                <Box>
                  {/* Court Event Information */}
                  <Card sx={{ mb: 3 }}>
                    <CardContent>
                      <Stack justifyContent={"space-between"} direction="row" alignItems="center" mb={2}>
                        <Typography variant="h6" color="primary" gutterBottom>
                          Event Information
                        </Typography>
                        <Stack direction="row" spacing={1}>
                          {!isEditing && selectedEvent && (
                            <>
                              <Tooltip title="Edit Event">
                                <IconButton 
                                  onClick={handleEditEvent}
                                  disabled={saving}
                                  size="small"
                                >
                                  <Edit />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete Event">
                                <IconButton 
                                  onClick={handleDeleteConfirm}
                                  disabled={saving}
                                  size="small"
                                  color="error"
                                >
                                  <Delete />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Stack>
                      </Stack>
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            Title
                          </Typography>
                          <Typography variant="body1">
                            {selectedEvent.CourtEvent_Title || 'No title'}
                          </Typography>
                          {selectedEvent.CourtEvent_CourtDifficulty && (
                            <Box mt={0.5}>
                              <CourtDifficultyChip difficulty={selectedEvent.CourtEvent_CourtDifficulty} />
                            </Box>
                          )}
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            Event ID
                          </Typography>
                          <Typography variant="body1">
                            {selectedEvent.CourtEvent_guid}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            Start Time
                          </Typography>
                          <Typography variant="body1">
                            {formatDateTime(selectedEvent.CourtEvent_Start)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            End Time
                          </Typography>
                          <Typography variant="body1">
                            {formatDateTime(selectedEvent.CourtEvent_End)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            Description
                          </Typography>
                          <Typography variant="body1">
                            {selectedEvent.CourtEvent_Desc || 'No description'}
                          </Typography>
                        </Box>
                        {/* Lloyd, Aug 10: the answer has to be the same wherever it is read, and
                            the calendar is where staff look when a third party rings about a date. */}
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Does the client need to appear?
                          </Typography>
                          <AppearanceControl
                            courtEventGuid={selectedEvent.CourtEvent_guid}
                            value={selectedEvent.CourtEvent_AppearanceRequirement ?? 'NOT_REQUIRED'}
                            setBy={selectedEvent.CourtEvent_AppearanceSetBy}
                            setAt={selectedEvent.CourtEvent_AppearanceSetAt}
                            isHearing={selectedEvent.CourtEvent_IsHearing ?? true}
                            startEpoch={selectedEvent.CourtEvent_Start}
                            disabled={saving}
                            onChanged={(next) =>
                              setSelectedEvent({
                                ...selectedEvent,
                                CourtEvent_AppearanceRequirement: next,
                              })
                            }
                          />
                        </Box>
                        {/* Lloyd, 21 Oct 2026: texted to him with the 15-minute reminder. */}
                        {(selectedEvent.CourtEvent_IsHearing ?? true) && (
                          <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                              Login / link (virtual hearings)
                            </Typography>
                            <HearingLoginControl
                              courtEventGuid={selectedEvent.CourtEvent_guid}
                              value={selectedEvent.CourtEvent_LoginInfo}
                              disabled={saving}
                              onChanged={(next) =>
                                setSelectedEvent({ ...selectedEvent, CourtEvent_LoginInfo: next })
                              }
                            />
                          </Box>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>

                  {/* Ticket Information */}
                  {fullEventData?.ticket && (
                    <Card>
                      <CardContent>
                        <Typography variant="h6" color="primary" gutterBottom>
                          Associated Ticket
                        </Typography>
                        <Stack spacing={2}>
                          <Box>
                            <Typography variant="subtitle2" color="text.secondary">
                              Ticket Number
                            </Typography>
                            <Typography variant="body1">
                              {fullEventData.ticket.Ticket_Number}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="subtitle2" color="text.secondary">
                              Ticket ID
                            </Typography>
                            <Typography variant="body1">
                              {fullEventData.ticket.Ticket_guid}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="subtitle2" color="text.secondary">
                              Citation
                            </Typography>
                            <Typography variant="body1">
                              {fullEventData.ticket.Ticket_Citation}
                            </Typography>
                          </Box>
                          {fullEventData?.user && (
                            <Box>
                              <Typography variant="subtitle2" color="text.secondary">
                                Client Name
                              </Typography>
                              <Typography variant="body1">
                                {fullEventData.user.User_FirstName} {fullEventData.user.User_LastName}
                              </Typography>
                            </Box>
                          )}
                        </Stack>
                        {fullEventData?.ticket?.Ticket_guid && (
                          <Button
                            component={Link}
                            to={`/ticket?Ticket_guid=${fullEventData.ticket.Ticket_guid}`}
                            color="primary"
                            variant="outlined"
                            disabled={saving}
                            sx={{ mt: 2, mr: 'auto' }} // Push to the left side
                          >
                            View Ticket
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          {isEditing ? (
            <>
              <Button onClick={handleCancelEdit} disabled={saving}>
                Cancel
              </Button>
              <Button 
                onClick={handleSaveEvent} 
                color="primary" 
                variant="contained"
                disabled={saving || !isCourtEventFormValid()}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleCloseModal} color="primary" disabled={saving}>
                Close
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteConfirmOpen} 
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">
            Delete Court Event
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete this court event? This action cannot be undone.
          </Typography>
          {selectedEvent && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Event to delete:
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {selectedEvent.CourtEvent_Title || 'Untitled Event'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatDateTime(selectedEvent.CourtEvent_Start)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteConfirmOpen(false)} 
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteEvent}
            color="error"
            variant="contained"
            disabled={saving}
          >
            {saving ? 'Deleting...' : 'Delete Event'}
          </Button>
        </DialogActions>
      </Dialog>

      {/*
        A non-case entry. Much smaller than the case modal above, because almost none of it applies:
        there is no client, no cause number, no appearance question and no documents.

        Read-only unless this is the viewer's own. `canEdit` comes from the backend, which compares
        the author against the session — the screen never holds anyone's identity to decide it.
      */}
      <Dialog
        open={selectedEntry !== null}
        onClose={() => !entrySaving && setSelectedEntry(null)}
        maxWidth="sm"
        fullWidth
      >
        {selectedEntry && (
          <>
            <DialogTitle>
              {CALENDAR_ENTRY_KIND_LABELS[selectedEntry.CalendarEntry_Kind]}
            </DialogTitle>
            <DialogContent>
              {entryError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEntryError(null)}>
                  {entryError}
                </Alert>
              )}
              <Typography variant="h6" sx={{ mb: 0.5 }}>
                {selectedEntry.CalendarEntry_Title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {selectedEntry.CalendarEntry_AllDay
                  ? DateTime.fromMillis(selectedEntry.CalendarEntry_Start).toFormat('cccc d LLLL yyyy')
                  : `${DateTime.fromMillis(selectedEntry.CalendarEntry_Start).toFormat('cccc d LLLL yyyy, h:mm a')} — ${DateTime.fromMillis(selectedEntry.CalendarEntry_End).toFormat('h:mm a')}`}
                {selectedEntry.CalendarEntry_CreatedByName
                  ? ` · ${selectedEntry.CalendarEntry_CreatedByName}`
                  : ''}
              </Typography>
              {selectedEntry.CalendarEntry_Desc && (
                <Typography variant="body2">{selectedEntry.CalendarEntry_Desc}</Typography>
              )}
              {!selectedEntry.canEdit && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                  Only the person who made this entry can change it.
                </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedEntry(null)} disabled={entrySaving}>
                Close
              </Button>
              {selectedEntry.canEdit && (
                <Button
                  color="error"
                  variant="contained"
                  disabled={entrySaving}
                  onClick={async () => {
                    setEntrySaving(true);
                    setEntryError(null);
                    try {
                      await CalendarEntryApi.remove(selectedEntry.CalendarEntry_guid);
                      setSelectedEntry(null);
                      props.onEntryChanged?.();
                    } catch (err) {
                      setEntryError(err instanceof Error ? err.message : 'Failed to remove it');
                    } finally {
                      setEntrySaving(false);
                    }
                  }}
                >
                  {entrySaving ? 'Removing…' : 'Remove'}
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default CalendarEditable;